import { createSlice } from '@reduxjs/toolkit'
import { DEFAULT_BOTTLE_MODE, GAME_PHASE } from '../constants'
import { getMyBets, getTopBets } from './balloonPumpGame.thunk'

const initialState = {
  selectedAmount: 0.1,
  currentScore: 0.1,
  myBetsData: [],
  topBetsData: [],
  myBetsLoading: false,
  topBetsLoading: false,
  isHolding: false,
  betLock: false,
  selectedMode: DEFAULT_BOTTLE_MODE,
  gamePhase: GAME_PHASE.IDLE,
  currentMultiplier: 1,
  potentialWin: 0,
  lastWinAmount: 0,
  insufficientFunds: false,
  autoplayRunning: false,
  autoplayRemaining: 0,
  autoplayCollectAt: 0
}

const mergePagedBets = (current, incoming, offset) => {
  if (!offset) return incoming
  const updated = [...current]
  incoming.forEach((item, i) => {
    updated[offset + i] = item
  })
  return updated
}

const bottleManiaGameSlice = createSlice({
  name: 'bottleManiaGame',
  initialState,
  reducers: {
    setSelectedAmount: (state, action) => {
      state.selectedAmount = action.payload
    },
    setCurrentScore: (state, action) => {
      state.currentScore = action.payload
    },
    setIsHolding: (state, action) => {
      state.isHolding = action.payload
    },
    setBetLock: (state, action) => {
      state.betLock = action.payload
    },
    setSelectedMode: (state, action) => {
      state.selectedMode = action.payload
    },
    setGamePhase: (state, action) => {
      state.gamePhase = action.payload
    },
    setCurrentMultiplier: (state, action) => {
      state.currentMultiplier = action.payload
    },
    setPotentialWin: (state, action) => {
      state.potentialWin = action.payload
    },
    setLastWinAmount: (state, action) => {
      state.lastWinAmount = action.payload
    },
    setInsufficientFunds: (state, action) => {
      state.insufficientFunds = action.payload
    },
    setAutoplayRunning: (state, action) => {
      state.autoplayRunning = action.payload
    },
    setAutoplayRemaining: (state, action) => {
      state.autoplayRemaining = action.payload
    },
    setAutoplayCollectAt: (state, action) => {
      state.autoplayCollectAt = action.payload
    },
    appendRoundBet: (state, action) => {
      const bet = action.payload
      if (!bet) return
      state.myBetsData = [bet, ...state.myBetsData]
      const winAmount = Number(bet.winningAmount ?? bet.winAmount ?? 0)
      if (winAmount > 0) {
        state.topBetsData = [bet, ...state.topBetsData]
          .sort((a, b) => Number(b.winningAmount ?? b.winAmount ?? 0) - Number(a.winningAmount ?? a.winAmount ?? 0))
      }
    },
    resetRoundVisuals: (state) => {
      state.gamePhase = GAME_PHASE.IDLE
      state.currentMultiplier = 1
      state.potentialWin = 0
      state.isHolding = false
      state.betLock = false
    },
    stopAutoplay: (state) => {
      state.autoplayRunning = false
      state.autoplayRemaining = 0
    },
    resetBottleManiaGame: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyBets.pending, (state) => {
        state.myBetsLoading = true
      })
      .addCase(getMyBets.rejected, (state) => {
        state.myBetsData = initialState.myBetsData
        state.myBetsLoading = false
      })
      .addCase(getMyBets.fulfilled, (state, action) => {
        const bets = action.payload?.bets ?? []
        const offset = action.payload?.offset || 0
        state.myBetsData = mergePagedBets(state.myBetsData, bets, offset)
        state.myBetsLoading = false
      })
      .addCase(getTopBets.pending, (state) => {
        state.topBetsLoading = true
      })
      .addCase(getTopBets.fulfilled, (state, action) => {
        const bets = action.payload?.bets ?? []
        const offset = action.payload?.offset || 0
        state.topBetsData = mergePagedBets(state.topBetsData, bets, offset)
        state.topBetsLoading = false
      })
      .addCase(getTopBets.rejected, (state) => {
        state.topBetsData = []
        state.topBetsLoading = false
      })
  }
})

export const {
  setSelectedAmount,
  setCurrentScore,
  setIsHolding,
  setBetLock,
  setSelectedMode,
  setGamePhase,
  setCurrentMultiplier,
  setPotentialWin,
  setLastWinAmount,
  setInsufficientFunds,
  setAutoplayRunning,
  setAutoplayRemaining,
  setAutoplayCollectAt,
  appendRoundBet,
  resetRoundVisuals,
  stopAutoplay,
  resetBottleManiaGame
} = bottleManiaGameSlice.actions

export default bottleManiaGameSlice.reducer
